import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { CreditCard, Plus, CheckCircle, AlertCircle, Loader2, Users, X, RefreshCw, XCircle, FileText, ShoppingCart } from 'lucide-react';
import subscriptionService from '../../services/subscription';
import type { OrganisationSubscription, SubscriptionPlan, BillingInfo, CustomerTerm } from '../../types/subscription';
import { describeTerm } from '../../services/billing';
import { useAuth } from '../../context/AuthContext';
import { useOrgTimezone, formatLocalDateInTz } from '@/utils/timezone';
import { isNativePlatform } from '@/utils/platform';
import { isOrgAdmin } from '@/utils/auth';

function SubscriptionManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgTz = useOrgTimezone();

  const [subscriptions, setSubscriptions] = useState<OrganisationSubscription[]>([]);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [terms, setTerms] = useState<CustomerTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cancel / reactivate state
  const [cancelTarget, setCancelTarget] = useState<OrganisationSubscription | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Platform RBAC checks for the active org. Source-of-truth lives on
  // user.orgs[active].roles — `user.roles` is a deprecated convenience copy.
  const isAdmin = isOrgAdmin(user);
  // Billing / purchase UI is web-only for App Store / Play Store compliance.
  const native = isNativePlatform();

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organisation_id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [subsResponse, plansResponse, billingResponse, termsResponse] = await Promise.all([
        subscriptionService.getOrganisationSubscriptions(),
        subscriptionService.getAvailablePlans(),
        isAdmin ? subscriptionService.getBillingInfo() : Promise.resolve({ success: false, data: null }),
        isAdmin && !native
          ? subscriptionService.getOrganisationTerms().catch(() => ({ success: false, data: [] as CustomerTerm[] }))
          : Promise.resolve({ success: false, data: [] as CustomerTerm[] }),
      ]);
      if (termsResponse.success) setTerms(termsResponse.data ?? []);

      if (subsResponse.success) setSubscriptions(subsResponse.data);
      if (plansResponse.success) setAvailablePlans(plansResponse.data);
      if (billingResponse.success && billingResponse.data) setBilling(billingResponse.data);
    } catch (err: any) {
      console.error('Failed to load subscriptions:', err);
      if (err?.status === 403) {
        navigate('/dashboard', { replace: true });
        return;
      }
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  // Count of assigned seats across all tiers for a subscription — used in
  // the confirm dialog to tell the admin exactly how many licenses will
  // be revoked.
  const totalAssignedSeats = (sub: OrganisationSubscription | null): number => {
    if (!sub) return 0;
    return (sub.seats ?? []).reduce((sum, s) => sum + (s.assigned_seats || 0), 0);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setActionLoading(cancelTarget.id);
    setActionError(null);
    try {
      const res = await subscriptionService.cancelSubscription(cancelTarget.id, {
        reason: cancelReason.trim() || undefined,
      });
      if (res.success) {
        setCancelTarget(null);
        setCancelReason('');
        await loadData();
      } else {
        setActionError('Failed to cancel subscription');
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.error?.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(null);
    }
  };

  // Reactivating is buying the module again: the purchase page preselects
  // it, and batch-purchase reactivates the cancelled row in place — and
  // invoices it. It used to open the platform operator's manual-activation
  // modal (invoice reference, start/end dates), which activated for free.
  const handleReactivate = (sub: OrganisationSubscription) => {
    navigate(`/subscriptions/purchase?plan=${sub.plan_id}`);
  };

  const getSubscriptionStatus = (sub: OrganisationSubscription) => {
    const statusColors = {
      active: 'bg-success-100 text-success-700 border-success-200',
      trial: 'bg-warning-100 text-warning-700 border-warning-200',
      suspended: 'bg-warning-100 text-warning-700 border-warning-200',
      cancelled: 'bg-error-100 text-error-700 border-error-200',
    };

    return statusColors[sub.status] || 'bg-neutral-100 text-neutral-700 border-neutral-200';
  };

  const isSubscribed = (planId: number) => {
    return subscriptions.some(s => s.plan_id === planId && s.status === 'active');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Subscription Management"
          subtitle="Manage your organisation's module subscriptions and licenses"
          icon={<CreditCard className="h-6 w-6 text-white" />}
        />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="h-10 w-10 text-primary-600 animate-spin mx-auto mb-4" />
            <p className="text-neutral-600">Loading subscriptions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Subscription Management"
        subtitle="Manage your organisation's module subscriptions and licenses"
        icon={<CreditCard className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-error-600" />
              <p className="text-error-700">{error}</p>
            </div>
          </div>
        )}

        {/* Billing Summary (Admin Only) — web only: hides pricing on native
            for App Store / Play Store compliance (Guideline 3.1.1). */}
        {isAdmin && billing && !native && (
          <div className="mb-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold">Current Billing</h2>
                <p className="text-primary-100">Monthly recurring charges</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">
                  {subscriptionService.formatCurrency(billing.total_monthly_cost, billing.currency)}
                </div>
                <p className="text-primary-100">per month</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-primary-400">
              <div>
                <p className="text-primary-100 text-sm">Active Modules</p>
                <p className="text-2xl font-semibold">{subscriptions.filter(s => s.status === 'active').length}</p>
              </div>
              <div>
                <p className="text-primary-100 text-sm">Total Seats</p>
                <p className="text-2xl font-semibold">
                  {subscriptions.filter(s => s.status !== 'cancelled').reduce((sum, s) => sum + (s.total_licenses || 0), 0)}
                </p>
              </div>
              <div>
                <p className="text-primary-100 text-sm">Assigned Seats</p>
                <p className="text-2xl font-semibold">
                  {subscriptions.filter(s => s.status !== 'cancelled').reduce((sum, s) => sum + (s.assigned_licenses || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Offers arranged with us — web only, with the rest of the pricing. */}
        {isAdmin && !native && terms.length > 0 && (
          <div className="mb-8 bg-primary-50 border border-primary-200 rounded-xl p-4">
            <h3 className="font-semibold text-primary-900 mb-1">Your offers</h3>
            <ul className="text-sm text-primary-800 space-y-0.5">
              {terms.map(t => <li key={t.id}>{describeTerm(t)}</li>)}
            </ul>
            <p className="text-xs text-primary-700 mt-2">Applied to your invoices automatically. Where more than one fits, the best one is used.</p>
          </div>
        )}

        {/* Quick Actions (Admin Only — web only) */}
        {isAdmin && !native && (
          <div className="mb-8 flex flex-wrap gap-3">
            <Button variant="primary" onClick={() => navigate('/subscriptions/purchase')}>
              <ShoppingCart className="h-4 w-4" />
              Add a Module
            </Button>
            <Button variant="outline" onClick={() => navigate('/subscriptions/invoices')}>
              <FileText className="h-4 w-4" />
              Invoice History
            </Button>
          </div>
        )}
        {/* No web-purchase steering on native (Guideline 3.1.1): admins manage
            billing through their organisation's account; the app does not direct
            users to an external purchase flow. */}

        {/* Active Subscriptions */}
        {subscriptions.filter(s => s.status !== 'cancelled').length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Your Subscriptions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subscriptions.filter(s => s.status !== 'cancelled').map((sub) => (
                <div
                  key={sub.id}
                  className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-semibold text-neutral-900">{sub.name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSubscriptionStatus(sub)}`}>
                        {sub.status}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-500">Module</span>
                        <span className="font-medium text-neutral-900">{sub.module_key}</span>
                      </div>

                      {/* Per-license-type seat breakdown */}
                      {sub.seats && sub.seats.length > 0 ? (
                        <div className="space-y-1.5 pt-1">
                          {sub.seats.map((seat) => (
                            <div key={seat.license_type_id} className="flex items-center justify-between text-sm">
                              <span className="text-neutral-500">{seat.name}</span>
                              <span className={`font-medium ${seat.assigned_seats === seat.total_seats && seat.total_seats > 0 ? 'text-warning-700' : 'text-neutral-900'}`}>
                                {seat.assigned_seats} / {seat.total_seats}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-neutral-500">Seats</span>
                          <span className="font-medium text-neutral-900">
                            {sub.assigned_licenses} / {sub.total_licenses}
                          </span>
                        </div>
                      )}

                      {!native && sub.trial_ends_at && !sub.trial_converted_at && sub.status === 'active' && (
                        <div className="bg-warning-50 border border-warning-200 rounded-lg p-2 text-xs text-warning-800">
                          Free trial until {formatLocalDateInTz(sub.trial_ends_at.slice(0, 10), orgTz)}. It then continues as a paid
                          subscription{isAdmin ? ' — cancel before then to stop it' : ''}.
                        </div>
                      )}

                      {sub.end_date && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-neutral-500">Renewal</span>
                          <span className="font-medium text-neutral-900">
                            {formatLocalDateInTz(sub.end_date, orgTz)}
                          </span>
                        </div>
                      )}

                      {isAdmin && !native && sub.base_price != null && (
                        <div className="pt-3 border-t border-neutral-100">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-neutral-500">Monthly Cost</span>
                            <span className="text-lg font-bold text-neutral-900">
                              {subscriptionService.formatCurrency(
                                Number(sub.base_price) +
                                (sub.seats ?? []).reduce((sum, s) => sum + (Number(s.price_per_seat) * s.total_seats), 0)
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {isAdmin && (
                      <div className="mt-4 pt-4 border-t border-neutral-100 flex flex-col gap-2">
                        {!native && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(`/subscriptions/${sub.id}/licenses`)}
                          >
                            <Users className="h-4 w-4" />
                            Seats &amp; Users
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-error-600 border-error-200 hover:bg-error-50"
                          onClick={() => {
                            setCancelTarget(sub);
                            setCancelReason('');
                            setActionError(null);
                          }}
                          disabled={actionLoading === sub.id}
                        >
                          <XCircle className="h-4 w-4" />
                          Cancel Subscription
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancelled Subscriptions — web only: the only action is Reactivate,
            which is a purchase flow, so it stays off native (Guideline 3.1.1). */}
        {isAdmin && !native && subscriptions.filter(s => s.status === 'cancelled').length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Cancelled Subscriptions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subscriptions.filter(s => s.status === 'cancelled').map(sub => (
                <div
                  key={sub.id}
                  className="bg-white rounded-xl shadow-sm border border-error-200 overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-neutral-900">{sub.name}</h3>
                        <p className="text-xs text-neutral-500 mt-1">
                          All user licenses were revoked when this subscription was cancelled.
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSubscriptionStatus(sub)}`}>
                        cancelled
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-success-700 border-success-200 hover:bg-success-50"
                      onClick={() => handleReactivate(sub)}
                      disabled={actionLoading === sub.id}
                    >
                      {actionLoading === sub.id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <RefreshCw className="h-4 w-4" />}
                      Reactivate Subscription
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {actionError && (
              <div className="mt-4 bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {actionError}
              </div>
            )}
          </div>
        )}

        {/* Available Plans — web only: this is a catalogue of purchasable
            modules with pricing, so it is hidden on native to comply with
            Guideline 3.1.1 (no purchasable digital content / pricing in-app). */}
        {!native && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-neutral-900">Available Modules</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {availablePlans.map((plan) => {
              const subscribed = isSubscribed(plan.id);
              const subscription = subscriptions.find(s => s.plan_id === plan.id);

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden ${
                    subscribed ? 'border-success-200' : 'border-neutral-200'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-neutral-900 mb-1">{plan.name}</h3>
                        <p className="text-sm text-neutral-600">{plan.description}</p>
                      </div>
                      {subscribed && (
                        <CheckCircle className="h-6 w-6 text-success-600 flex-shrink-0" />
                      )}
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-neutral-900">
                          {subscriptionService.formatCurrency(plan.base_price)}
                        </span>
                        <span className="text-neutral-500">/month base</span>
                      </div>
                      {plan.license_types && plan.license_types.length > 0 && (
                        <div className="text-sm text-neutral-500 mt-2 space-y-0.5">
                          {plan.license_types.map((lt) => (
                            <div key={lt.id} className="flex items-center justify-between">
                              <span>{lt.name}</span>
                              <span className="font-medium">
                                + {subscriptionService.formatCurrency(Number(lt.price_per_seat))}/seat
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {plan.features && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Features:</h4>
                        <ul className="space-y-1 text-sm text-neutral-600">
                          {Object.entries(plan.features).map(([key, value]) => (
                            <li key={key} className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-success-600 flex-shrink-0 mt-0.5" />
                              <span>{key.replace(/_/g, ' ')}: {String(value)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {isAdmin && !native && (
                      <Button
                        variant={subscribed ? 'outline' : 'primary'}
                        disabled={subscribed}
                        className="w-full"
                        onClick={() => !subscribed && navigate(`/subscriptions/purchase?plan=${plan.id}`)}
                      >
                        {subscribed ? (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            Active Subscription
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4" />
                            Activate Module
                          </>
                        )}
                      </Button>
                    )}
                    {isAdmin && native && subscribed && (
                      <div className="w-full bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-700 text-center">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Active Subscription
                      </div>
                    )}

                    {!isAdmin && subscribed && subscription && (
                      <div className="bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-700 text-center">
                        Your organisation has access to this module
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* No Admin Message */}
        {!isAdmin && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Administrator Access Required</h3>
                <p className="text-sm text-blue-700">
                  Contact your organisation administrator to manage subscriptions or assign licenses to users.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Subscription confirmation modal */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-neutral-200 flex items-start gap-3">
              <div className="bg-error-100 p-2 rounded-lg flex-shrink-0">
                <XCircle className="h-5 w-5 text-error-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Cancel {cancelTarget.name}?
                </h3>
                <p className="text-sm text-neutral-500 mt-1">
                  This immediately revokes access for every user in your organisation.
                </p>
              </div>
              <button
                onClick={() => {
                  setCancelTarget(null);
                  setCancelReason('');
                  setActionError(null);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">
                      {totalAssignedSeats(cancelTarget)} user license
                      {totalAssignedSeats(cancelTarget) === 1 ? '' : 's'} will be revoked.
                    </p>
                    <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                      <li>All seat counts reset to 0</li>
                      <li>Features stop working for every licensed user</li>
                      <li>Auto-renew is turned off</li>
                      <li>You can reactivate later, but seats and assignments need to be re-created</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Per-tier breakdown if we have it */}
              {cancelTarget.seats && cancelTarget.seats.length > 0 && (
                <div className="text-xs text-neutral-600 space-y-1">
                  {cancelTarget.seats
                    .filter(s => s.assigned_seats > 0)
                    .map(s => (
                      <div key={s.license_type_id} className="flex justify-between">
                        <span>{s.name}</span>
                        <span className="font-medium">{s.assigned_seats} assigned</span>
                      </div>
                    ))}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. Non-payment, feature no longer required, switching plans…"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Recorded in the subscription transaction history for your records.
                </p>
              </div>

              {actionError && (
                <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {actionError}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-neutral-200 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setCancelTarget(null);
                  setCancelReason('');
                  setActionError(null);
                }}
                disabled={actionLoading === cancelTarget.id}
              >
                Keep Subscription
              </Button>
              <Button
                variant="primary"
                className="flex-1 !bg-error-600 hover:!bg-error-700"
                onClick={handleConfirmCancel}
                disabled={actionLoading === cancelTarget.id}
              >
                {actionLoading === cancelTarget.id ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cancelling…
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    Yes, Cancel & Revoke Licenses
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionManagement;
